import { get, filter } from 'lodash'
import { countLandedImages, requiredImages } from '../routes/single-submission/utils'
import { UNREADABLE_FILE_REASON } from '../routes/single-submission/utils/readDroppedFiles'

/**
 * STY-725: pure mapping from builder state to typed, PII-free funnel props
 * (spec R10). File names, typed text and emails never leave the browser, only
 * counts and reason codes.
 */

export function httpStatusOf (error) {
  const status = get(error, 'response.status', get(error, 'statusCode'))
  return Number.isInteger(status) ? status : undefined
}

export function imageRejectionReason (reason) {
  const text = String(reason || '')
  if (text === UNREADABLE_FILE_REASON) return 'decode_failed'
  if (text.indexOf('too small') !== -1) return 'too_narrow'
  if (text.indexOf('too large') !== -1) return 'too_large'
  if (text.indexOf('not a valid format') !== -1) return 'bad_format'
  return 'other'
}

// One entry per reason, in first-seen order.
export function rejectionCounts (files) {
  const counts = []
  ;(files || []).forEach((file) => {
    const reason = imageRejectionReason(file && file.reason)
    const entry = counts.find((c) => c.reason === reason)
    if (entry) entry.count++
    else counts.push({ reason, count: 1 })
  })
  return counts
}

// Files the dropzone refused before reading (oversize or not an image),
// grouped by reason. Recorded before the credentials check (Codex on #113).
export function dropzoneRejectionCounts (rejected) {
  const counts = []
  ;(rejected || []).forEach((entry) => {
    const file = (entry && entry.file) || {}
    const reason = file.size > 10485760 ? 'too_large' : String(file.type || '').indexOf('image') === -1 ? 'bad_format' : 'other'
    const group = counts.find((c) => c.reason === reason)
    if (group) group.count++
    else counts.push({ reason, count: 1 })
  })
  return counts
}

// Upload-queue failures grouped by where they failed. Photos the vendor deleted
// mid-upload aren't failures.
//  - Errors tagged uploadStage 'register' (or carrying an HTTP response) failed
//    registering the image row.
//  - Anything else failed at S3, which is where expired credentials show up.
export function uploadFailures (failed) {
  const groups = []
  ;(failed || []).forEach((entry) => {
    const error = entry && entry.error
    if (error && error.deletedDuringUpload) return
    // The builder tags registration failures explicitly, because an axios timeout
    // or network error has no response either (Codex P2, #113).
    const stage = error && (error.uploadStage === 'register' || error.response) ? 'register' : 's3'
    const group = groups.find((g) => g.stage === stage)
    if (group) group.count++
    else groups.push({ stage, count: 1, http_status: httpStatusOf(error) })
  })
  return groups
}

// Validation errors from Yup (abortEarly: false) name the field; anything else
// is the server refusing the save.
export function creditRejectReason (error) {
  const paths = (get(error, 'inner') || []).map((e) => e.path)
  if (!paths.length) return 'server_error'
  if (paths.indexOf('url') !== -1) return 'url_invalid'
  if (paths.indexOf('type') !== -1) return 'missing_type'
  if (paths.indexOf('name') !== -1) return 'missing_name'
  return 'other'
}

function promptCounts (additionalInfo) {
  let required = 0
  let filled = 0
  ;(get(additionalInfo, 'data.sections') || []).forEach((section) => {
    ;(section.fields || []).forEach((field) => {
      if (field.type !== 'Required') return
      required++
      if (field.text && field.text.length) filled++
    })
  })
  return { required, filled }
}

export function minutesSince (date, now = Date.now()) {
  const t = Date.parse(date)
  return Number.isNaN(t) ? undefined : Math.max(0, Math.floor((now - t) / 60000))
}

export function progressSnapshot ({ submission, credits, images, additionalInfo }, now = Date.now()) {
  const prompts = promptCounts(additionalInfo)
  const minutes = minutesSince(get(submission, 'created'), now)
  return {
    images: countLandedImages(images),
    images_required: requiredImages(submission || {}),
    credits: filter(credits || [], Boolean).length,
    prompts_filled: prompts.filled,
    prompts_required: prompts.required,
    title_set: Boolean(get(submission, 'title')),
    location_set: Boolean(get(submission, 'location') && String(submission.location).trim()),
    days_since_created: minutes === undefined ? undefined : Math.floor(minutes / 1440)
  }
}

// In-app navigation from the dashboard carries router state (STY-725, Codex on
// #113): React Router links never update document.referrer.
export function entrySource ({ publicHash, referrer, routerFrom }) {
  if (publicHash) return 'collab_link'
  if (routerFrom === 'dashboard') return 'dashboard'
  if (!referrer) return 'direct'
  if (/stylemepretty\.com\/submissions\/dashboard/.test(referrer)) return 'dashboard'
  return 'other'
}

// The dashboard count is the account total only when nothing narrows the list:
// no status filter and no publication filter (filters.type or notFilters.type).
// The default notFilters.status=collection is the dashboard's own baseline, not
// a vendor choice (local Codex on #113).
export function accountTotal (filters, notFilters, count) {
  const narrowed = Boolean(get(filters, 'status') || get(filters, 'type') || get(notFilters, 'type'))
  return narrowed ? undefined : count
}

// Text the vendor is typing. Prompt and title fields save on blur, so a field
// edited but not yet blurred holds work no save queue knows about (local Codex
// on #113). Checkboxes, selects and file inputs save on change and don't count.
const TEXT_INPUT_TYPES = ['', 'text', 'search', 'url', 'email', 'tel', 'number']
export function isTextEntry (el) {
  if (!el || !el.tagName) return false
  if (el.isContentEditable) return true
  const tag = String(el.tagName).toLowerCase()
  if (tag === 'textarea') return true
  return tag === 'input' && TEXT_INPUT_TYPES.indexOf(String(el.getAttribute ? el.getAttribute('type') || '' : el.type || '').toLowerCase()) !== -1
}

// Forms that DON'T save on blur, only on a button (local Codex and review on #113):
// - the add-credit form and the video link (data-funnel-draft="text") clear
//   themselves once saved, so any text left in them is a draft never added;
// - the photographer credit in the image modal starts filled in, so it says
//   itself whether it differs from what's saved (data-funnel-draft="dirty").
export const DRAFT_ONLY_SELECTOR = '[data-funnel-section="credits"], [data-funnel-draft]'
const TEXT_DRAFTS = '[data-funnel-section="credits"], [data-funnel-draft="text"]'

export function hasDraftText (doc) {
  if (!doc || !doc.querySelectorAll) return false
  if (doc.querySelector && doc.querySelector('[data-funnel-draft="dirty"]')) return true
  return Array.prototype.some.call(doc.querySelectorAll(TEXT_DRAFTS), (root) =>
    Array.prototype.some.call(root.querySelectorAll('input, textarea'), (el) => isTextEntry(el) && String(el.value || '').trim() !== ''))
}
